import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  Box,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Typography,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  ArrowBackIosNew as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIcon,
  PersonOutline as PersonIcon,
  AccessTime as TimeIcon,
} from "@mui/icons-material";

import { useSidebar } from "../../context/SidebarContext";
import { useBreadcrumb } from "../../context/BreadcrumbContext";
import { AppDispatch } from "../../store";
import { fetchMonitoringShifts } from "../../modules/shift/actions";
import { selectMonitoringShifts, selectMonitoringShiftsCount } from "../../modules/shift/selectors";
import { fetchFacilityList } from "../../modules/facility/actions";
import { fetchWorkerList } from "../../modules/worker/actions";

import {
  PageContainer,
  SearchWrapper,
  StyledInput,
  BoardContainer,
  FiltersContainer,
  DateNavigator,
  DateText,
  MonitoringGrid,
  GridHeaderCell,
  ShiftCard,
  CardTime,
  CardRow,
  CardText,
  LegendContainer,
  LegendItem,
  LegendColor,
  LegendLabel,
  CornerHeader,
  TimeCell,
  GridCell,
} from "./styles";
import CreateShiftModal from "./components/CreateShiftModal";
import ShiftDetailContent from "./components/ShiftDetailContent";
import DetailModal from "../../components/DetailModal";
import AlertPopup from "../../components/common/AlertPopup";
import PaginatedSearchSelect from "../../components/PaginatedSearchSelect";

import CustomDateRangePicker from "../../components/CustomDateRangePicker";

const PAGE_SIZE = 2000;

// Batches for Infinite Scroll (3 batches of 8 hours)
// const BATCHES = [
//   [0, 1, 2, 3, 4, 5, 6, 7],            // 12AM - 7AM
//   [8, 9, 10, 11, 12, 13, 14, 15],      // 8AM - 3PM
//   [16, 17, 18, 19, 20, 21, 22, 23]     // 4PM - 11PM
// ];

// Helper to format hour label (e.g., 13 -> 1 PM)
const formatHourLabel = (hour: number) => {
  return dayjs().hour(hour).minute(0).format("h A");
};

interface Option {
  id: number;
  label: string;
}

const OrgShiftMonitoringPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { setActivePage, setIsCollapsed } = useSidebar();
  const { setIsShow } = useBreadcrumb();

  // Redux State
  const shifts = useSelector(selectMonitoringShifts);
  const totalCount = useSelector(selectMonitoringShiftsCount);

  const [currentDate, setCurrentDate] = useState(dayjs().day() === 0 ? dayjs().subtract(6, 'day').startOf('day') : dayjs().startOf('week').add(1, 'day'));
  const [page, setPage] = useState(1);
  const [viewType, setViewType] = useState<"fortnight" | "week" | "month" | "custom">("week");
  const [customRange, setCustomRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFacility, setSelectedFacility] = useState<Option | null>(null);
  const [selectedWorker, setSelectedWorker] = useState<Option | null>(null);
  const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null);
  const [selectedShiftStatus, setSelectedShiftStatus] = useState<string>("");
  const [selectedShiftFacilityName, setSelectedShiftFacilityName] = useState<string>("");
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [editShiftData, setEditShiftData] = useState<any>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<{ date: dayjs.Dayjs, time: dayjs.Dayjs } | null>(null);

  // Infinite Scroll State
  const gridRef = useRef<HTMLDivElement>(null);
  // Render all hours 0-23 to ensure full grid height for scrolling
  const fullDayHours = Array.from({ length: 24 }, (_, i) => i);
  const [visibleHours] = useState<number[]>(fullDayHours);

  // Track loading state to prevent duplicate calls
  const isLoading = useSelector((state: any) => state.monitoringShifts?.loading);

  const [alert, setAlert] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  // Update selectedShiftStatus if the shift data changes (sync header chip)
  useEffect(() => {
    if (selectedShiftId && shifts) {
      const currentShift = shifts.find(s => s.id === selectedShiftId);
      if (currentShift && currentShift.status !== selectedShiftStatus) {
        setSelectedShiftStatus(currentShift.status);
      }
    }
  }, [shifts, selectedShiftId, selectedShiftStatus]);

  // Calculate actual start Date and end Date based on viewType
  const { actualStart, actualEnd, daysToShow } = useMemo(() => {
    let start = currentDate;
    let end: dayjs.Dayjs;
    let days = 7;

    if (viewType === 'custom' && customRange[0] && customRange[1]) {
      start = customRange[0];
      end = customRange[1];
      days = end.diff(start, 'day') + 1;
    } else {
      if (viewType === 'fortnight') days = 14;
      else if (viewType === 'month') days = 30; // 30 days window for month view
      else days = 7; // 'week' fallback

      end = start.add(days - 1, 'day');
    }

    return { actualStart: start, actualEnd: end, daysToShow: Math.max(days, 1) }; // Ensure at least 1 day
  }, [viewType, currentDate, customRange]);

  // Format dates for API
  const startDate = actualStart.format('YYYY-MM-DD');
  const endDateStr = actualEnd.format('YYYY-MM-DD');

  // Fetch Data on Change
  useEffect(() => {
    dispatch(fetchMonitoringShifts({
      start_date: startDate,
      end_date: endDateStr,
      facility: selectedFacility ? selectedFacility.id : undefined,
      worker_id: selectedWorker ? selectedWorker.id : undefined,
      search: searchTerm,
      page_size: PAGE_SIZE,
      page: page
    }));
  }, [dispatch, startDate, endDateStr, selectedFacility, selectedWorker, searchTerm, page]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [startDate, endDateStr, selectedFacility, selectedWorker, searchTerm]);

  // Scroll Handler for Grid (Infinite Loading)
  useEffect(() => {
    const gridElement = gridRef.current;
    if (!gridElement) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight, scrollLeft, scrollWidth, clientWidth } = gridElement;

      // Check vertical scroll
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        if (!isLoading) {
          const maxPage = Math.ceil(totalCount / PAGE_SIZE);
          if (page < maxPage) {
            setPage(prev => prev + 1);
          }
        }
      }

      // Check horizontal scroll
      if (scrollLeft + clientWidth >= scrollWidth - 50) {
        if (!isLoading) {
          const maxPage = Math.ceil(totalCount / PAGE_SIZE);
          if (page < maxPage) {
            setPage(prev => prev + 1);
          }
        }
      }
    };

    gridElement.addEventListener('scroll', handleScroll);
    return () => gridElement.removeEventListener('scroll', handleScroll);
  }, [page, totalCount, isLoading]);

  // Using Redux Data
  // Map API data to component safe shape if needed, currently direct mapping mostly works
  // Map API data to component safe shape
  const allShifts = React.useMemo(() => shifts.map(s => ({
    ...s,
    id: s.id.toString(),
    start: s.start,
    end: s.end,
    workerName: (s.worker_details && Array.isArray(s.worker_details) && s.worker_details.length > 0)
      ? (s.worker_details.length > 1 ? `${s.worker_details.length} Workers Assigned` : s.worker_details[0].name)
      : (s.shift_assignment && s.shift_assignment.length > 0 && s.shift_assignment[0].worker?.user
        ? `${s.shift_assignment[0].worker.user.first_name} ${s.shift_assignment[0].worker.user.last_name}`
        : s.assigned_workers || "Unconfirmed"),
    workerId: (s.worker_details && Array.isArray(s.worker_details) && s.worker_details.length > 0)
      ? (s.worker_details.length > 1 ? "-" : s.worker_details[0].id)
      : (s.shift_assignment && s.shift_assignment.length > 0 && s.shift_assignment[0].worker
        ? s.shift_assignment[0].worker.worker_id
        : "-"),
    role: s.role_name || (Array.isArray(s.worker_details) && s.worker_details.length > 0 ? s.worker_details[0].role : null) || "-", // Fallback
    facilityName: s.facility_name,
    hours: dayjs(s.end).diff(dayjs(s.start), "hour", true).toFixed(1),
    apiStatus: s.status, // Preserve original API status
    status: (() => {
      // Direct Statuses from Backend
      if (['rejected', 'partially_rejected'].includes(s.status)) return s.status;

      const isPast = dayjs(s.end).isBefore(dayjs());

      // Check for facility_reject in worker_details
      if (s.worker_details && Array.isArray(s.worker_details)) {
        if (s.worker_details.some((w: any) => w.shift_status === 'facility_reject')) {
          return 'facility_reject';
        }
      }

      // Check for strictly defined pending statuses that should NOT default to 'unassigned'
      if (['partially_approval_pending', 'approval_pending'].includes(s.status)) {
        return 'facility_approval_pending';
      }

      if (!s.worker_details && s.status !== 'cancelled' && !s.status.includes('completed')) return 'unassigned';

      if (s.status === 'partially_absent') return 'partially_absent';

      const isAssignedOnly = ['assigned', 'scheduled', 'upcoming', 'pending', 'absent'].includes(s.status);
      if (s.worker_details && isPast && isAssignedOnly) {
        return 'absent';
      }

      if (isPast && s.status !== 'cancelled' && s.status !== 'unassigned') return 'completed';

      // Normalize started-type statuses to 'started' for consistent coloring
      if (['ongoing', 'clocked_in'].includes(s.status)) return 'started';

      return s.status;
    })(),
    is_sleepover: s.is_sleepover,
    label: s.label,
  })), [shifts]);

  useEffect(() => {
    setActivePage("shift");
    setIsShow(false);
    setIsCollapsed(true); // Automatically collapse sidebar on load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePrevDays = () => {
    if (viewType === 'month') setCurrentDate(currentDate.subtract(30, 'day'));
    else if (viewType === 'fortnight') setCurrentDate(currentDate.subtract(14, 'day'));
    else if (viewType === 'week') setCurrentDate(currentDate.subtract(7, 'day'));
    else if (viewType === 'custom' && customRange[0] && customRange[1]) {
      const days = customRange[1].diff(customRange[0], 'day') + 1;
      setCustomRange([customRange[0].subtract(days, 'day'), customRange[1].subtract(days, 'day')]);
    }
  };

  const handleNextDays = () => {
    if (viewType === 'month') setCurrentDate(currentDate.add(30, 'day'));
    else if (viewType === 'fortnight') setCurrentDate(currentDate.add(14, 'day'));
    else if (viewType === 'week') setCurrentDate(currentDate.add(7, 'day'));
    else if (viewType === 'custom' && customRange[0] && customRange[1]) {
      const days = customRange[1].diff(customRange[0], 'day') + 1;
      setCustomRange([customRange[0].add(days, 'day'), customRange[1].add(days, 'day')]);
    }
  };

  // Helper to generate header days
  const days: dayjs.Dayjs[] = [];
  for (let i = 0; i < daysToShow; i++) {
    days.push(actualStart.add(i, 'day'));
  }

  const endDate = days[days.length - 1] || actualEnd;
  const dateRangeString = `${actualStart.format('DD/MM/YYYY')} - ${endDate.format('DD/MM/YYYY')}`;

  // Filter shifts
  const getShiftsForDate = (date: dayjs.Dayjs) => {
    return allShifts.filter(shift =>
      dayjs(shift.start).isSame(date, 'day')
      // Client validation/double check
      && (!selectedFacility || shift.facilityName === selectedFacility.label)
      && (searchTerm === "" || shift.workerName.toLowerCase().includes(searchTerm.toLowerCase()) || shift.facilityName.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  const handleGridCellClick = (date: dayjs.Dayjs, hour: number) => {
    setEditShiftData(null); // Clear edit data for new shift
    const time = dayjs(date).hour(hour).minute(0);
    setSelectedSlot({ date, time });
    setIsModalOpen(true);
  };

  const handleEditShift = (shift: any) => {
    setEditShiftData(shift);
    setIsDetailModalOpen(false);
    setIsModalOpen(true);
  };

  const refreshGrid = () => {
    dispatch(fetchMonitoringShifts({
      start_date: startDate,
      end_date: endDateStr,
      facility: selectedFacility ? selectedFacility.id : undefined,
      worker_id: selectedWorker ? selectedWorker.id : undefined,
      search: searchTerm,
      page_size: PAGE_SIZE,
      page: page
    }));
  };

  const handleShiftCreateSuccess = (msg?: string) => {
    setAlert({ message: msg || (editShiftData ? "Shift updated successfully!" : "Shift(s) created successfully!"), severity: "success" });
    setEditShiftData(null); // Reset edit mode
    // Refresh Data
    refreshGrid();
  };

  // Adapters for PaginatedSearchSelect
  const fetchFacilityOptions = React.useCallback(async (query: string, page: number) => {
    try {
      const response: any = await dispatch(fetchFacilityList({
        page: page,
        pageSize: 20,
        search: query
      })).unwrap();

      const options = response.results.map((f: any) => ({
        id: f.facility_id,
        label: f.facility_name
      }));

      return {
        options,
        hasMore: response.next !== null // Or calculate based on count
      };
    } catch (error) {
      console.error("Failed to fetch facilities", error);
      return { options: [], hasMore: false };
    }
  }, [dispatch]);

  const fetchWorkerOptions = React.useCallback(async (query: string, page: number) => {
    try {
      const response: any = await dispatch(fetchWorkerList({
        page: page,
        pageSize: 20,
        searchTerm: query,
        status: 'active'
      })).unwrap();

      const options = response.results.map((w: any) => ({
        id: w.worker_id,
        label: `${w.user.first_name} ${w.user.last_name}`
      }));

      return {
        options,
        hasMore: response.count > page * 20
      };
    } catch (error) {
      console.error("Failed to fetch workers", error);
      return { options: [], hasMore: false };
    }
  }, [dispatch]);

  return (
    <PageContainer>
      {/* Header */}


      <BoardContainer elevation={0}>
        {/* Filters Row */}
        <FiltersContainer>
          <DateNavigator>
            <IconButton size="small" onClick={handlePrevDays}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <DateText>{dateRangeString}</DateText>
            <IconButton size="small" onClick={handleNextDays}>
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </DateNavigator>

          <Box display="flex" alignItems="center" gap={1.5}>
            <Box width={120}>
              <FormControl fullWidth size="small" sx={{ position: 'relative' }}>
                <Select
                  value={viewType}
                  onChange={(e) => {
                    const newView = e.target.value as any;
                    setViewType(newView);
                    if (newView === 'week') {
                      setCurrentDate(dayjs().startOf('week').add(1, 'day'));
                    } else if (newView === 'month') {
                      setCurrentDate(dayjs().startOf('month'));
                    } else if (newView === 'fortnight') {
                      setCurrentDate(dayjs().day() === 0 ? dayjs().subtract(6, 'day').startOf('day') : dayjs().startOf('week').add(1, 'day'));
                    }
                  }}
                  displayEmpty
                  sx={{ borderRadius: '8px', backgroundColor: '#fff', height: '36px', fontSize: '0.85rem' }}
                  MenuProps={{
                    disablePortal: true,
                    autoFocus: false,
                    sx: {
                      position: 'absolute !important',
                      top: '100% !important',
                      left: '0 !important',
                      right: '0 !important',
                      width: '100% !important',
                      transform: 'none !important',
                      marginTop: '4px',
                      zIndex: 1300,
                      '& .MuiPaper-root': {
                        position: 'relative !important',
                        top: 'auto !important',
                        left: 'auto !important',
                        transform: 'none !important',
                        maxHeight: 300,
                      }
                    }
                  }}
                >
                  <MenuItem value="fortnight">Fortnight</MenuItem>
                  <MenuItem value="week">Week</MenuItem>
                  <MenuItem value="month">Month</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {viewType === "custom" && (
              <Box width={240}>
                <CustomDateRangePicker
                  value={customRange}
                  onChange={(newValue) => setCustomRange(newValue)}
                />
              </Box>
            )}

            <SearchWrapper>
              <StyledInput
                placeholder="Search by Name, ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <IconButton
                  size="small"
                  onClick={() => setSearchTerm('')}
                  sx={{ padding: '2px', marginRight: '4px' }}
                >
                  <ClearIcon sx={{ color: '#9ca3af', fontSize: 18 }} />
                </IconButton>
              )}
              <SearchIcon sx={{ color: '#9ca3af', fontSize: 18 }} />
            </SearchWrapper>

            <Box width={200}>
              <PaginatedSearchSelect<Option>
                label=""
                placeholder="Search Facility"
                fetchOptions={fetchFacilityOptions}
                getOptionLabel="label"
                value={selectedFacility}
                onChange={(newValue) => setSelectedFacility(newValue)}
              />
            </Box>

            <Box width={200}>
              <PaginatedSearchSelect<Option>
                label=""
                placeholder="Search Worker"
                fetchOptions={fetchWorkerOptions}
                getOptionLabel="label"
                value={selectedWorker}
                onChange={(newValue) => setSelectedWorker(newValue)}
              />
            </Box>
          </Box>
        </FiltersContainer>

        {/* Grid */}
        <MonitoringGrid ref={gridRef} daysCount={daysToShow}>
          {/* Header Row */}
          <CornerHeader>
            <TimeIcon sx={{ color: '#fff', fontSize: 20 }} />
          </CornerHeader>

          {/* Date Headers */}
          {days.map((day) => (
            <GridHeaderCell key={day.toString()}>
              {day.format("ddd D")}
            </GridHeaderCell>
          ))}

          {/* Body Rows (Iterate by Visible Hour Batches) */}
          {/* Using BATCHES for display structure, but data is populated via API pagination */}
          {visibleHours.map((hour) => (
            <React.Fragment key={hour}>
              {/* Time Cell (Left Column) */}
              <TimeCell key={`time-${hour}`}>
                {formatHourLabel(hour)}
              </TimeCell>

              {/* Day Cells for this Hour */}
              {days.map((day) => {
                const dayShifts = getShiftsForDate(day);
                // Filter shifts starting in this specific hour
                const shiftsInHour = dayShifts.filter(shift => {
                  const startHour = dayjs(shift.start).hour();
                  return startHour === hour;
                });

                return (
                  <GridCell
                    key={`cell-${day.format('DD')}-${hour}`}
                    onClick={() => handleGridCellClick(day, hour)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f9fafb' }
                    }}
                  >
                    {shiftsInHour.map((shift) => (
                      <ShiftCard
                        key={shift.id}
                        status={shift.status}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedShiftId(Number(shift.id));
                          setSelectedShiftStatus(shift.status);
                          setSelectedShiftFacilityName(shift.facilityName);
                          setIsDetailModalOpen(true);
                        }}
                      >
                        <CardTime>
                          {dayjs(shift.start).format("h:mm A")} - {dayjs(shift.end).format("h:mm A")}
                        </CardTime>
                        <CardRow>
                          <PersonIcon sx={{ fontSize: 14, opacity: 0.9 }} />
                          <CardText>{shift.workerName}</CardText>
                        </CardRow>
                        <CardRow>
                          <TimeIcon sx={{ fontSize: 14, opacity: 0.9 }} />
                          <CardText>{shift.hours} hours</CardText>
                        </CardRow>
                        <CardText sx={{ marginTop: '4px', fontSize: '12px', fontWeight: 600, textTransform: 'capitalize' }}>
                          {shift.status === 'facility_reject'
                            ? 'Facility Reject'
                            : shift.status === 'rejected'
                              ? 'Rejected'
                              : shift.status === 'partially_rejected'
                                ? 'Partially Rejected'
                                : shift.status === 'facility_approval_pending'
                                  ? 'Facility Approval Pending'
                                  : shift.apiStatus === 'upcoming' ? 'Scheduled' : shift.apiStatus === 'unassigned' ? 'Unconfirmed' : shift.apiStatus.replace(/_/g, ' ')}
                        </CardText>
                        {shift.is_sleepover && (
                          <CardText sx={{ marginTop: '4px', fontWeight: 800, color: '#fff', backgroundColor: 'rgba(0,0,0,0.2)', padding: '2px 4px', borderRadius: '4px', width: 'fit-content', fontSize: '10px' }}>
                            SLEEPOVER SHIFT
                          </CardText>
                        )}
                        {shift.label && (
                          <CardRow sx={{ mt: 0.5 }}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#fff' }}>
                              {shift.label}
                            </Typography>
                          </CardRow>
                        )}
                        <CardText sx={{ marginTop: '4px', fontStyle: 'italic', fontSize: '11px' }}>
                          {shift.facilityName}
                        </CardText>
                      </ShiftCard>
                    ))}
                  </GridCell>
                );
              })}
            </React.Fragment>
          ))}
        </MonitoringGrid>

        {/* Legend */}
        <LegendContainer>
          {[
            { label: "Completed", color: "#479577" },
            { label: "Started", color: "#66bb6a" },
            { label: "Facility Approval Pending", color: "#8D6E63" },
            { label: "Scheduled", color: "#4A90E2" },
            { label: "Unconfirmed", color: "#D2A627" },
            { label: "Cancelled", color: "#FF4D4F" },
            { label: "Absent", color: "#FF9800" },
            { label: "Rejected", color: "#C62828" },
          ].map((item) => (
            <LegendItem key={item.label}>
              <LegendColor color={item.color} />
              <LegendLabel>{item.label}</LegendLabel>
            </LegendItem>
          ))}
        </LegendContainer>



      </BoardContainer >

      <CreateShiftModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleShiftCreateSuccess}
        initialDate={selectedSlot?.date}
        initialTime={selectedSlot?.time}
        initialFacility={selectedFacility}
        editData={editShiftData}
      />
      <DetailModal
        open={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        shiftId={selectedShiftId || 0}
        shiftStatus={selectedShiftStatus}
      >
        <ShiftDetailContent
          shiftId={selectedShiftId}
          facilityName={selectedShiftFacilityName}
          onEdit={handleEditShift}
          onRefresh={refreshGrid}
        />
      </DetailModal>
      <AlertPopup
        open={!!alert}
        message={alert?.message || ''}
        severity={alert?.severity || 'success'}
        onClose={() => setAlert(null)}
      />
    </PageContainer >
  );
};

export default OrgShiftMonitoringPage;
